import { type PartialPropsOf, type PropsOf } from '@blaze-runtime/engine-domain';
import { type PropertiesConfig } from '@blaze-shared/elements-domain';
import { getValueByPath, isAggregateNode } from '@blaze-shared/workflow-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import {
  AGGREGATION_OPTIONS,
  CHART_DIMENSIONS,
  type ChartPropsModel,
  DATE_FORMATS_OPTIONS,
  DATE_PERIOD_OPTIONS,
  MONTH_FORMATS_OPTIONS,
  NUMBER_FIELD_FORMATS,
} from './chart.models';
import { isComputeNodeForChart } from './utils/chart-data.utils';
import {
  onlyDateWithMultipleLevels,
  preventSelectingColumnForCount,
} from './utils/chart-select-options.utils';

export function getPropsConfig(type: 'chartPie' | 'chartDoughnut'): PropertiesConfig {
  return epc.initConfig(type, [
    epc.section('content', 'Content', [
      ...[
        epc.subsections.subsection(
          'data.?key:value.enabled',
          '',
          [
            epc.fields.selectDataSourceField({
              key: 'value',
              label: 'Data Source',
              sources: 'list',
              bindings: [],
            }),
          ],
          {
            labelWidth: '40%',
            hideLabel: true,
          },
        ),
        epc.subsections.subsection(
          'data.?key:value.enabled',
          'Show',
          [
            epc.utils.observedField(
              epc.fields.selectDataValueField(
                'Y Axis',
                ['content.data.aggregation', 'content.data.mainValue'],
                [AGGREGATION_OPTIONS, 'data.?key:value.dataSource'],
                {
                  dataBindingsMode: 'first-dataset',
                  allowSubSelection: true,
                  filterColumnsType: ['Formula', 'Number'],
                  emptyListPlaceholder: 'No numeric fields',
                  allowOpenStepsPredicate: preventSelectingColumnForCount,
                  backBtnLabel: 'Back to Aggregation Methods',
                  stepsTagViewUse: [true],
                  nodePredicate: isComputeNodeForChart,
                  hideLabel: true,
                  placeholder: 'Select',
                },
              ),
              (model: ChartPropsModel, value: string) => {
                if (model.content.data?.groupBy === value) {
                  return { 'content.data.groupBy': model.content.data?.mainValue || '' };
                } else if (model.content.data?.segmentData === value) {
                  return { 'content.data.segmentData': model.content.data?.mainValue || '' };
                } else if (value === 'COUNT') {
                  return { 'content.data.mainValue': '' };
                }
                return {};
              },
            ),
            epc.utils.conditionalField(
              epc.fields.toggleField('content.chart.showTotal', 'Show Total'),
              '',
              () => type !== 'chartDoughnut',
            ),
          ],
          {
            hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
          },
        ),
        epc.subsections.subsection(
          'data.?key:value.enabled',
          'Segment By',
          [
            epc.utils.observedField(
              epc.fields.selectDataValueField(
                'Y Axis',
                ['content.data.segmentData', 'content.data.segmentDataDetails'],
                [DATE_PERIOD_OPTIONS, 'data.?key:value.dataSource'],
                {
                  dataBindingsMode: 'first-dataset',
                  allowSubSelection: true,
                  emptyListPlaceholder: 'No fields',
                  backBtnLabel: 'Back',
                  allowOpenStepsPredicate: onlyDateWithMultipleLevels,
                  preventSelectingValueFromPaths: ['content.data.mainValue'],
                  reverseOrder: true,
                  subSelectionForColumnType: ['Date', 'DateTime'],
                  nodePredicate: isComputeNodeForChart,
                  alternativeNodePredicate: isAggregateNode,
                  hideLabel: true,
                  placeholder: 'Select',
                },
              ),
              (model: ChartPropsModel, value: string) => {
                return {
                  ...(model.content.data?.groupBy === value ? { 'content.data.groupBy': '' } : {}),
                  ...(value ? { 'content.data.order': 'x' } : {}),
                };
              },
            ),
          ],
          {
            hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
          },
        ),
        epc.subsections.subsection(
          'data.?key:value.enabled',
          'Number Format',
          [
            epc.fields.selectFieldFromList(
              'content.format.type',
              'Type',
              NUMBER_FIELD_FORMATS.map((v) => ({ label: v.toString(), value: v.toString() })),
              {
                showSelection: true,
                allowNone: false,
              },
            ),
            epc.fields.currencyConditionalField('content.format.currency', 'content.format.type'),
            epc.fields.numberPrecisionField('content.format.precision'),
            epc.utils.conditionalField(
              epc.fields.selectFieldFromList(
                'content.format.dateXFormat',
                'Date format',
                DATE_FORMATS_OPTIONS,
                {
                  showSelection: true,
                  allowNone: false,
                },
              ),
              'content.data.segmentDataDetails',
              (target) => !['daily', 'weekly'].includes(target ?? ''),
            ),
            epc.utils.conditionalField(
              epc.fields.selectFieldFromList(
                'content.format.dateXFormat',
                'Date format',
                MONTH_FORMATS_OPTIONS,
                {
                  showSelection: true,
                  allowNone: false,
                },
              ),
              'content.data.segmentDataDetails',
              (target) => target !== 'monthly',
            ),
          ],
          {
            hideWhen: (_, __, model: ChartPropsModel) => !getValueByPath(model, 'data.?key:value'),
          },
        ),
      ],
      epc.subsections.elementSizeSubsection(
        CHART_DIMENSIONS.minSize,
        CHART_DIMENSIONS.maxWidth,
        CHART_DIMENSIONS.maxHeight,
        CHART_DIMENSIONS.widthOptions,
        CHART_DIMENSIONS.heightOptions,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content', [
        epc.fields.textInput('content.title.description', 'Description'),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.chart', 'Chart colors', [
          epc.fields.colorField('style.chart.line', 'Line', {
            allowGradient: false,
            allowSolid: true,
          }),
          epc.fields.tagsColorPaletteField('style.chart.palette', 'Series', {
            bindingKey: 'optionsList',
            hideSelector: true,
            hideForeColor: true,
            editablePalette: true,
            palettes: [{ label: 'Palette 2', value: 'palette2' }],
          }),
        ]),
        epc.subsections.subsection('style.background', 'Background', [
          epc.fields.colorField('style.background.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.background.outline', 'Outline', {
            allowGradient: false,
            allowSolid: true,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.background.width', 'Width', [
            { min: 0, max: 25, unit: 'px' },
          ]),
          epc.fields.radiusField('style.background.radius'),
          ...epc.fields.visualEffectField('style.background.visualEffect'),
        ]),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          descriptionColor: 'style.general.description',
          fontFamily: 'content.font.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'content.font.style',
          fontSize: 'content.font.fontSize',
          horizontalAlign: 'style.general.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

export function getDefaults(
  title: string,
  type: 'chartPie' | 'chartDoughnut',
): PropsOf<'chartPie' | 'chartDoughnut'> {
  return {
    content: {
      chart: {
        ...(type === 'chartDoughnut' && { showTotal: true }),
      },
      title: {
        label: title,
        description: '',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      format: {
        type: 'Number',
        precision: null,
        currency: 'USD',
      },
      data: {
        order: 'x',
        direction: 'Ascending',
        minValue: undefined,
        maxValue: undefined,
      },
      font: {
        style: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        lineHeight: '$t.lineHeight',
      },
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        description: { color1: '$t.value', selectorType: 'solid' },
        align: 'left',
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingCharts' },
      },
      titleBlock: {
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      chart: {
        line: { color1: '$t.outline', selectorType: 'none' },
        palette: { paletteName: 'palette2' },
      },
      background: {
        fill: { color1: '$t.fill', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        width: '2px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      alert: {
        neutral: { color1: '#F8B918', selectorType: 'solid' },
        danger: { color1: '#EF305E', selectorType: 'solid' },
        success: { color1: '#6ABA4E', selectorType: 'solid' },
      },
    },
  };
}

export function getInitial(): PartialPropsOf<'chartPie' | 'chartDoughnut'> {
  return {
    content: {
      chart: {
        hideLegendAfterLines: 2,
      },
    },
    style: {
      background: {
        width: '$t.containerOutlineWidth',
      },
    },
  };
}
